/**
 * The entry form: one block per field (label, outlined control, hint, divider), as in the Figma edit frame.
 * Fields edit their parent object, so `field_suffix` translations (`title_en` next to `title`) can be bound too.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { defaultsFor, isRequired, listItemFields, parseYouTube, renderTemplate, switchOptionOf, type Field, type Problem, type SwitchOption } from '@hotpot/core';
import { LOCALE_NAMES, useT, type T } from '../i18n.js';
import { useAsync, useSite, useToast } from '../state.js';
import { errorText } from '../screens/Login.js';
import { hasTranslatable, useEditor } from './context.js';
import { GalleryControl, ImageControl } from './Images.js';
import { RichText } from './RichText.js';

type Obj = Record<string, any>;

export function problemText(p: Problem, t: T): string {
  if (p.code === 'pattern' && p.message) return p.message;
  return t(`v_${p.code}` as Parameters<T>[0], { limit: p.limit ?? '' });
}

export function FieldList({ fields, parent, onParent, path, skip = [], top = false }: { fields: Field[]; parent: Obj; onParent: (p: Obj) => void; path: string; skip?: string[]; top?: boolean }) {
  const { t } = useT();
  const ctx = useEditor();
  const translating = ctx.locale !== ctx.defaultLocale;
  let list = fields.filter((f) => f.type !== 'hidden' && !skip.includes(f.name));
  if (translating) list = list.filter((f) => hasTranslatable(f, ctx.locale, ctx.i18nMode));
  const main = top && !translating ? list.filter((f) => !f.advanced) : list;
  const advanced = top && !translating ? list.filter((f) => f.advanced) : [];
  const advancedHasError = advanced.some((f) => ctx.problems.some((p) => p.path === join(path, f.name) || p.path.startsWith(join(path, f.name) + '.')));
  return (
    <>
      {main.map((f) => (
        <FieldView key={f.name} field={f} parent={parent} onParent={onParent} path={join(path, f.name)} />
      ))}
      {advanced.length > 0 && (
        <details className="group field" open={advancedHasError || undefined}>
          <summary className="muted">{t('moreOptions')}</summary>
          {advanced.map((f) => (
            <FieldView key={f.name} field={f} parent={parent} onParent={onParent} path={join(path, f.name)} />
          ))}
        </details>
      )}
    </>
  );
}

function join(a: string, b: string | number) {
  return a ? `${a}.${b}` : String(b);
}

function FieldView({ field, parent, onParent, path }: { field: Field; parent: Obj; onParent: (p: Obj) => void; path: string }) {
  const { t, lang } = useT();
  const ctx = useEditor();
  const value = parent?.[field.name];
  const set = (v: unknown) => onParent({ ...parent, [field.name]: v });
  const problems = ctx.problems.filter((p) => p.path === path);
  const translating = ctx.locale !== ctx.defaultLocale;
  const id = `f-${path}`;

  if (field.type === 'object') {
    const inner = (
      <div className="nested">
        {field.switch ? (
          <ObjectSwitch field={field} value={value ?? {}} onChange={(v) => set(v)} path={path} />
        ) : (
          <FieldList fields={field.fields} parent={value ?? {}} onParent={(v) => set(v)} path={path} />
        )}
      </div>
    );
    const hasError = ctx.problems.some((p) => p.path.startsWith(path + '.'));
    return (
      <div className="field" id={id}>
        {field.collapsed && !hasError ? (
          <details className="group">
            <summary>{field.label}</summary>
            {field.hint && <p className="hint" style={{ marginTop: -8, marginBottom: 14 }}>{field.hint}</p>}
            {inner}
          </details>
        ) : (
          <>
            <div className="label">{field.label}</div>
            {field.hint && <p className="hint" style={{ marginTop: -4, marginBottom: 14 }}>{field.hint}</p>}
            {inner}
          </>
        )}
      </div>
    );
  }

  // Side by side (i18n.layout): the main text and its translation together, both editable.
  const pair = ctx.sideBySide && !translating && ctx.i18nMode === 'field_suffix' && ['string', 'text', 'markdown'].includes(field.type) ? Object.entries(field.translations ?? {}).find(([l]) => l !== ctx.defaultLocale) : undefined;
  if (pair) return <BilingualField field={field} parent={parent} onParent={onParent} id={id} path={path} toLocale={pair[0]} trKey={pair[1]} />;

  // Translating with title/title_en pairs: the main text for reference, the translation to edit.
  const trKey = translating && ctx.i18nMode === 'field_suffix' ? field.translations?.[ctx.locale] : undefined;
  if (trKey) {
    const defaultName = LOCALE_NAMES[ctx.defaultLocale]?.[lang === 'cs' ? 'cs' : 'en'] ?? ctx.defaultLocale;
    const trProblems = ctx.problems.filter((p) => p.path === join(path.split('.').slice(0, -1).join('.'), trKey));
    return (
      <div className="field" id={id}>
        <label htmlFor={`${id}-tr`}>
          {field.label} <span className="req">{ctx.locale.toUpperCase()}</span>
        </label>
        <div className="tr-pair">
          <div className="tr-ref" aria-label={ctx.defaultLocale.toUpperCase()}>
            {field.type === 'markdown' ? String(value ?? '').slice(0, 600) : String(value ?? '') || <span className="muted">{t('none')}</span>}
          </div>
          <Control field={{ ...field, required: false }} id={`${id}-tr`} value={parent?.[trKey]} onChange={(v) => onParent({ ...parent, [trKey]: v })} invalid={trProblems.length > 0} placeholder={typeof value === 'string' ? value.split('\n')[0] : undefined} />
        </div>
        <p className="hint">{t('translationFallback', { lang: defaultName })}</p>
        {trProblems.map((p, i) => (
          <p className="error-text" key={i} role="alert">
            {problemText(p, t)}
          </p>
        ))}
      </div>
    );
  }

  const wide = field.type === 'list' || field.type === 'gallery' || field.type === 'markdown';
  return (
    <div className="field" id={id}>
      {wide || field.type === 'boolean' ? (
        field.type === 'boolean' ? null : (
          <div className="label">
            {field.label}
            {!isRequired(field) && <span className="req">{t('optional')}</span>}
          </div>
        )
      ) : (
        <label htmlFor={id + '-c'}>
          {field.label}
          {!isRequired(field) && <span className="req">{t('optional')}</span>}
          {translating && ctx.i18nMode === 'multiple_files' && <span className="req">{ctx.locale.toUpperCase()}</span>}
        </label>
      )}
      {ctx.i18nMode === 'multiple_files' && translating && ctx.reference && typeof ctx.reference[field.name] === 'string' && (
        <div className="tr-ref" style={{ marginBottom: 8, fontSize: 16 }}>
          {String(ctx.reference[field.name]).slice(0, 400)}
        </div>
      )}
      <Control field={field} id={id + '-c'} value={value} onChange={set} invalid={problems.length > 0} path={path} />
      {field.hint && <p className="hint">{field.hint}</p>}
      {problems.map((p, i) => (
        <p className="error-text" key={i} role="alert">
          {problemText(p, t)}
        </p>
      ))}
    </div>
  );
}

type TrState = 'idle' | 'working' | 'auto' | 'off' | 'error';

/**
 * A text and its translation next to each other, both editable. With `auto_translate`, typing the main text fills in a
 * machine translation after a short pause, as long as the translation is empty or is still the machine's own (a
 * translation edited by hand is never overwritten, except with "Translate again").
 */
function BilingualField({ field, parent, onParent, id, path, toLocale, trKey }: { field: Field; parent: Obj; onParent: (p: Obj) => void; id: string; path: string; toLocale: string; trKey: string }) {
  const { t } = useT();
  const ctx = useEditor();
  const site = useSite();
  const value = parent?.[field.name];
  const tr = parent?.[trKey];
  const parentRef = useRef(parent);
  parentRef.current = parent;
  const trRef = useRef(tr);
  trRef.current = tr;
  const lastSource = useRef(typeof value === 'string' ? value : '');
  const lastAuto = useRef<string | undefined>(undefined);
  const [state, setState] = useState<TrState>('idle');
  const problems = ctx.problems.filter((p) => p.path === path);
  const trProblems = ctx.problems.filter((p) => p.path === join(path.split('.').slice(0, -1).join('.'), trKey));

  const run = async (src: string, alive: () => boolean) => {
    setState('working');
    try {
      const out = await site.handle.translate(src, ctx.defaultLocale, toLocale);
      if (!alive()) return;
      lastAuto.current = out;
      onParent({ ...parentRef.current, [trKey]: out || undefined });
      setState(out ? 'auto' : 'idle');
    } catch (e) {
      if (alive()) setState((e as { code?: string })?.code === 'translation_unavailable' ? 'off' : 'error');
    }
  };

  useEffect(() => {
    if (!ctx.autoTranslate) return;
    const src = typeof value === 'string' ? value : '';
    if (src === lastSource.current) return;
    lastSource.current = src;
    const cur = typeof trRef.current === 'string' ? trRef.current : '';
    if (cur && cur !== lastAuto.current) return; // written by hand: leave it
    if (!src.trim()) {
      if (cur) onParent({ ...parentRef.current, [trKey]: undefined });
      setState('idle');
      return;
    }
    let live = true;
    const timer = setTimeout(() => void run(src, () => live), 1200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const status = state === 'working' ? t('trWorking') : state === 'auto' ? t('trAuto') : state === 'off' ? t('trOff') : state === 'error' ? t('trFailed') : '';
  const src = typeof value === 'string' ? value : '';
  return (
    <div className="field" id={id}>
      <label htmlFor={`${id}-c`}>
        {field.label}
        {!isRequired(field) && <span className="req">{t('optional')}</span>}
      </label>
      <div className="tr-pair">
        <div className="tr-col">
          <span className="tr-tag">{ctx.defaultLocale.toUpperCase()}</span>
          <Control field={field} id={`${id}-c`} value={value} onChange={(v) => onParent({ ...parent, [field.name]: v })} invalid={problems.length > 0} path={path} />
        </div>
        <div className="tr-col">
          <span className="tr-tag">{toLocale.toUpperCase()}</span>
          <Control field={{ ...field, required: false }} id={`${id}-tr`} value={tr} onChange={(v) => onParent({ ...parent, [trKey]: v })} invalid={trProblems.length > 0} path={`${path}:${toLocale}`} />
        </div>
      </div>
      {(status || (ctx.autoTranslate && src.trim() && state !== 'off')) && (
        <div className="tr-status hint">
          {status && <span role="status">{status}</span>}
          {ctx.autoTranslate && src.trim() && state !== 'off' && state !== 'working' && (
            <button type="button" className="link-btn" onClick={() => void run(src, () => true)}>
              {t('trAgain')}
            </button>
          )}
        </div>
      )}
      {field.hint && <p className="hint">{field.hint}</p>}
      {[...problems, ...trProblems].map((p, i) => (
        <p className="error-text" key={i} role="alert">
          {problemText(p, t)}
        </p>
      ))}
    </div>
  );
}

type SwitchedFields = { label: string; fields: Field[]; switch?: SwitchOption[] };

/** An object (or list item) of several kinds (picture / video / link): a switch, then only the chosen kind's fields. */
function ObjectSwitch({ field, value, onChange: emit, path }: { field: SwitchedFields; value: Obj; onChange: (v: Obj) => void; path: string }) {
  const options = field.switch!;
  const [mode, setMode] = useState(() => switchOptionOf(options, value));
  // Values of the other kinds, kept while switching back and forth (they are not saved).
  const stash = useRef<Obj>({});
  // A value that didn't come from here (items reordered, a version restored): show the kind it is.
  const last = useRef(value);
  if (value !== last.current) {
    last.current = value;
    const kind = switchOptionOf(options, value);
    if (kind !== mode && options[kind]!.fields.some((n) => value?.[n] !== undefined && value?.[n] !== '')) {
      stash.current = {};
      setMode(kind);
    }
  }
  const onChange = (v: Obj) => {
    last.current = v;
    emit(v);
  };
  const all = new Set(options.flatMap((o) => o.fields));
  const choose = (i: number) => {
    if (i === mode) return;
    const keep = new Set(options[i]!.fields);
    const next: Obj = {};
    for (const [k, v] of Object.entries(value)) {
      if (!all.has(k) || keep.has(k)) next[k] = v;
      else stash.current[k] = v;
    }
    for (const k of keep) if (next[k] === undefined && stash.current[k] !== undefined) next[k] = stash.current[k];
    setMode(i);
    onChange(next);
  };
  const option = options[mode]!;
  // The chosen kind's fields in its own order and with its own labels, then fields that belong to no kind.
  const shown = [
    ...option.fields.map((n) => field.fields.find((f) => f.name === n)).filter((f): f is Field => !!f).map((f) => (option.labels?.[f.name] ? { ...f, label: option.labels[f.name]! } : f)),
    ...field.fields.filter((f) => !all.has(f.name)),
  ];
  return (
    <>
      <div className="chips switch-chips" role="radiogroup" aria-label={field.label} style={{ marginBottom: 18 }}>
        {options.map((o, i) => (
          <button type="button" role="radio" aria-checked={i === mode} key={o.label} className={`chip${i === mode ? ' on' : ''}`} onClick={() => choose(i)}>
            {o.label}
          </button>
        ))}
      </div>
      <FieldList fields={shown} parent={value} onParent={onChange} path={path} />
    </>
  );
}

function Control({ field, id, value, onChange, invalid, placeholder, path = '' }: { field: Field; id: string; value: any; onChange: (v: any) => void; invalid: boolean; placeholder?: string; path?: string }) {
  const { t } = useT();
  const cls = (base: string) => `${base}${invalid ? ' invalid' : ''}`;
  switch (field.type) {
    case 'string':
    case 'url':
    case 'color':
      return (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {field.type === 'color' && <input type="color" value={/^#[0-9a-f]{6}$/i.test(value ?? '') ? value : '#000000'} onChange={(e) => onChange(e.target.value)} aria-label={field.label} style={{ width: 44, height: 35, border: 0, background: 'none', padding: 0 }} />}
          <input id={id} className={cls('input')} type={field.type === 'url' ? 'url' : 'text'} inputMode={field.type === 'url' ? 'url' : undefined} value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid} maxLength={'max_length' in field ? field.max_length : undefined} />
        </div>
      );
    case 'youtube': {
      const vid = parseYouTube(value ?? '');
      return (
        <>
          <input id={id} className={cls('input')} type="url" inputMode="url" value={value ?? ''} placeholder={placeholder ?? 'https://www.youtube.com/watch?v=…'} onChange={(e) => onChange(e.target.value.trim())} aria-invalid={invalid} />
          {vid && (
            <div className="yt">
              <span className="status live small">
                <span className="dot" />
                {t('youtubeOk', { id: vid })}
              </span>
              {/* No embedded player or thumbnail: that would load YouTube (a third party) inside the CMS. */}
              <a href={`https://www.youtube.com/watch?v=${vid}`} target="_blank" rel="noopener noreferrer">
                {t('youtubeOpen')}
              </a>
            </div>
          )}
        </>
      );
    }
    case 'text':
      return <AutoTextarea id={id} className={cls('textarea')} value={value ?? ''} placeholder={placeholder} onChange={onChange} invalid={invalid} minRows={field.rows ?? 3} />;
    case 'markdown':
      return <RichText value={value ?? ''} onChange={onChange} label={field.label} invalid={invalid} />;
    case 'number':
      return (
        <input
          id={id}
          className={cls('input')}
          style={{ maxWidth: 220 }}
          type="number"
          inputMode={field.value_type === 'float' ? 'decimal' : 'numeric'}
          step={field.step ?? (field.value_type === 'float' ? 'any' : 1)}
          min={field.min}
          max={field.max}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          aria-invalid={invalid}
        />
      );
    case 'boolean':
      return (
        <label className="switch" htmlFor={id}>
          <input id={id} type="checkbox" className="check" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          <span>{field.label}</span>
        </label>
      );
    case 'date':
      return <input id={id} className={cls('input')} style={{ maxWidth: 260 }} type="date" value={String(value ?? '').slice(0, 10)} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid} />;
    case 'datetime':
      return <input id={id} className={cls('input')} style={{ maxWidth: 300 }} type="datetime-local" value={String(value ?? '').slice(0, 16)} onChange={(e) => onChange(e.target.value ? `${e.target.value}:00` : '')} aria-invalid={invalid} />;
    case 'select':
      return <SelectControl field={field} id={id} value={value} onChange={onChange} invalid={invalid} />;
    case 'relation':
      return <RelationControl field={field} id={id} value={value} onChange={onChange} invalid={invalid} />;
    case 'image':
      return <ImageControl field={field} value={value} onChange={onChange} />;
    case 'file':
      return <FileControl field={field} id={id} value={value} onChange={onChange} invalid={invalid} />;
    case 'gallery':
      return <GalleryControl field={field} value={value} onChange={onChange} />;
    case 'list':
      return <ListControl field={field} value={value} onChange={onChange} path={path} />;
    default:
      return <input id={id} className="input" value={typeof value === 'string' ? value : JSON.stringify(value ?? '')} readOnly />;
  }
}

/** A file kept outside the repository (a video in R2): its URL, with an upload button when the site has a bucket. */
function FileControl({ field, id, value, onChange, invalid }: { field: Extract<Field, { type: 'file' }>; id: string; value: any; onChange: (v: any) => void; invalid: boolean }) {
  const { t } = useT();
  const site = useSite();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const url = typeof value === 'string' ? value : '';
  const upload = async (file: File | undefined) => {
    if (!file || !field.storage) return;
    setProgress(0);
    try {
      onChange(await site.handle.uploadLarge(field.storage, file, setProgress));
    } catch (e) {
      toast(t('fileUploadFailed', { error: errorText(e, t) }));
    } finally {
      setProgress(null);
    }
  };
  return (
    <>
      <input id={id} className={`input${invalid ? ' invalid' : ''}`} type="url" inputMode="url" value={url} placeholder={t('fileUrlPlaceholder')} onChange={(e) => onChange(e.target.value.trim() || undefined)} aria-invalid={invalid} />
      <div className="yt">
        {field.storage && (
          <>
            <input ref={input} type="file" hidden accept={field.accept} onChange={(e) => void upload(e.target.files?.[0] ?? undefined).then(() => (e.target.value = ''))} />
            <button type="button" className="outline-btn" onClick={() => input.current?.click()} disabled={progress !== null}>
              {progress !== null ? t('uploadingPct', { n: Math.round(progress * 100) }) : url ? t('replace') : t('uploadFile')}
            </button>
          </>
        )}
        {/* A link only: playing the video inline would load it from the bucket's domain inside the CMS. */}
        {/^https:\/\//.test(url) && (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {t('openFile')}
          </a>
        )}
      </div>
    </>
  );
}

function AutoTextarea({ id, className, value, onChange, placeholder, invalid, minRows }: { id: string; className: string; value: string; onChange: (v: string) => void; placeholder?: string; invalid: boolean; minRows: number }) {
  const rows = Math.max(minRows, Math.min(18, String(value).split('\n').length + Math.floor(String(value).length / 90) + 1));
  return <textarea id={id} className={className} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid} />;
}

function SelectControl({ field, id, value, onChange, invalid }: { field: Extract<Field, { type: 'select' }>; id: string; value: any; onChange: (v: any) => void; invalid: boolean }) {
  const { t } = useT();
  if (field.multiple) {
    const list: unknown[] = Array.isArray(value) ? value : [];
    return (
      <div className="chips" role="group" aria-label={field.label} id={id}>
        {field.options.map((o) => {
          const on = list.map(String).includes(String(o.value));
          return (
            <button type="button" key={String(o.value)} className={`chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => onChange(on ? list.filter((v) => String(v) !== String(o.value)) : [...list, o.value])}>
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (field.options.length <= 4 && field.options.every((o) => o.label.length < 30)) {
    return (
      <div className="chips" role="radiogroup" aria-label={field.label} id={id}>
        {field.options.map((o) => (
          <button type="button" role="radio" aria-checked={String(value) === String(o.value)} key={String(o.value)} className={`chip${String(value) === String(o.value) ? ' on' : ''}`} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <select id={id} className={`select${invalid ? ' invalid' : ''}`} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? undefined : field.options.find((o) => String(o.value) === e.target.value)?.value)}>
      <option value="">{t('choose')}</option>
      {field.options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function RelationControl({ field, id, value, onChange, invalid }: { field: Extract<Field, { type: 'relation' }>; id: string; value: any; onChange: (v: any) => void; invalid: boolean }) {
  const { t } = useT();
  const site = useSite();
  const [q, setQ] = useState('');
  const options = useAsync(() => site.content.relationOptions(field), [field.collection, field.list, site.version]);
  if (!options.data) return <p className="muted small">{options.error ? options.error.message : t('loading')}</p>;
  if (field.multiple) {
    const list: string[] = Array.isArray(value) ? value.map(String) : [];
    const shown = options.data.filter((o) => !q || o.label.toLowerCase().includes(q.toLowerCase()) || list.includes(o.value));
    return (
      <>
        {options.data.length > 12 && <input className="input" style={{ maxWidth: 300, marginBottom: 10, minHeight: 31, fontSize: 16 }} placeholder={t('searchOptions')} value={q} onChange={(e) => setQ(e.target.value)} />}
        <div className="chips" role="group" aria-label={field.label} id={id}>
          {shown.map((o) => {
            const on = list.includes(o.value);
            return (
              <button type="button" key={o.value} className={`chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => onChange(on ? list.filter((v) => v !== o.value) : [...list, o.value])}>
                {o.label}
              </button>
            );
          })}
        </div>
      </>
    );
  }
  return (
    <select id={id} className={`select${invalid ? ' invalid' : ''}`} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
      <option value="">{t('none')}</option>
      {options.data.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {value && !options.data.some((o) => o.value === value) && <option value={value}>{value}</option>}
    </select>
  );
}

function ListControl({ field, value, onChange, path }: { field: Extract<Field, { type: 'list' }>; value: any; onChange: (v: any) => void; path: string }) {
  const { t } = useT();
  const ctx = useEditor();
  const items: any[] = Array.isArray(value) ? value : [];
  const translating = ctx.locale !== ctx.defaultLocale && ctx.i18nMode === 'field_suffix';
  // Open by default, so editors see what they are editing; a field can ask to start closed (`collapsed: true`).
  const [open, setOpen] = useState<Set<number>>(() => new Set(field.collapsed ? [] : items.map((_, i) => i)));
  const move = (i: number, d: number) => {
    const next = [...items];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x);
    onChange(next);
    setOpen(new Set([...open].map((n) => (n === i ? i + d : n === i + d ? i : n))));
  };
  const remove = (i: number) => {
    onChange(items.filter((_, n) => n !== i));
    setOpen(new Set([...open].filter((n) => n !== i).map((n) => (n > i ? n - 1 : n))));
  };
  const typeKey = field.type_key ?? 'type';
  const add = (kind?: string) => {
    const type = field.types?.find((x) => x.name === kind);
    const item = type ? { [typeKey]: type.name, ...defaultsFor(type.fields) } : field.fields ? defaultsFor(field.fields) : field.field?.default ?? '';
    onChange([...items, item]);
    setOpen(new Set([...open, items.length]));
  };
  const singular = field.label_singular ?? t('item');

  // A list of plain values (e.g. address lines): one input per line.
  if (!field.fields && field.field) {
    const sub = field.field;
    return (
      <div className="items">
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Control field={sub} id={`f-${path}.${i}`} value={item} onChange={(v) => onChange(items.map((x, n) => (n === i ? v : x)))} invalid={ctx.problems.some((p) => p.path === `${path}.${i}`)} />
            </div>
            {!translating && (
              <>
                <button type="button" className="icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('moveUp')}>↑</button>
                <button type="button" className="icon-btn" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={t('moveDown')}>↓</button>
                <button type="button" className="icon-btn" onClick={() => remove(i)} aria-label={t('remove')}>✕</button>
              </>
            )}
          </div>
        ))}
        {!translating && (
          <div>
            <button type="button" className="outline-btn" onClick={() => add()}>
              + {t('addItem', { name: singular })}
            </button>
          </div>
        )}
      </div>
    );
  }

  const summary = (item: any, i: number): ReactNode => {
    const type = field.types?.find((x) => x.name === item?.[typeKey]);
    const template = type ? type.summary : field.summary;
    const text = template ? renderTemplate(template, item ?? {}) : '';
    const fields = listItemFields(field, item);
    // Otherwise the first filled-in text, picture or file (by its file name).
    const first = fields?.find((f) => ['string', 'text', 'image', 'file'].includes(f.type) && typeof item?.[f.name] === 'string' && item[f.name]);
    const firstValue = first ? String(item[first.name]) : '';
    const own = text || (first && (first.type === 'image' || first.type === 'file') ? firstValue.split('/').pop()! : firstValue);
    if (type) return own && own !== type.label ? `${type.label} · ${own}` : type.label;
    return own || `${singular} ${i + 1}`;
  };

  return (
    <div className="items">
      {items.map((item, i) => {
        const itemPath = `${path}.${i}`;
        const hasError = ctx.problems.some((p) => p.path.startsWith(itemPath + '.') || p.path === itemPath);
        const isOpen = open.has(i) || hasError;
        return (
          <div className="item" key={i}>
            <div className="item-head">
              <span className="t" onClick={() => setOpen(new Set(isOpen ? [...open].filter((n) => n !== i) : [...open, i]))} role="button" tabIndex={0} aria-expanded={isOpen} onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLElement).click()}>
                <span className="muted" style={{ marginRight: 8 }}>{isOpen ? '–' : '+'}</span>
                {summary(item, i)}
                {hasError && <span style={{ color: 'var(--accent-ink)' }}> ●</span>}
              </span>
              {!translating && (
                <>
                  <button type="button" className="icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('moveUp')}>↑</button>
                  <button type="button" className="icon-btn" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={t('moveDown')}>↓</button>
                  <button type="button" className="icon-btn" onClick={() => remove(i)} aria-label={t('remove')}>
                    {t('remove')}
                  </button>
                </>
              )}
            </div>
            {isOpen && (
              <div className="item-body">
                {field.switch && field.fields ? (
                  <ObjectSwitch field={field as SwitchedFields} value={item ?? {}} onChange={(v) => onChange(items.map((x, n) => (n === i ? v : x)))} path={itemPath} />
                ) : listItemFields(field, item) ? (
                  <FieldList fields={listItemFields(field, item)!} parent={item ?? {}} onParent={(v) => onChange(items.map((x, n) => (n === i ? v : x)))} path={itemPath} />
                ) : (
                  <p className="hint">{t('unknownBlock', { type: String(item?.[typeKey] ?? '?') })}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
      {!translating && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {field.types ? (
            field.types.map((type) => (
              <button type="button" className="outline-btn" key={type.name} onClick={() => add(type.name)}>
                + {type.label}
              </button>
            ))
          ) : (
            <button type="button" className="outline-btn" onClick={() => add()}>
              + {t('addItem', { name: singular })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function useRootListField(fields: Field[]) {
  return useMemo(() => fields.find((f) => f.type === 'list' && f.root), [fields]);
}
